import { z } from 'zod';
import { nonEmptyPatch } from '../_patch.js';
import { CreateClientInputSchema } from '../clients.js';
import { phoneSchema } from '../phone.js';
import { LeadIntakeSubmissionHistoryItemSchema } from './call-center-intake.js';
import { LEAD_SOURCE_DISPLAY_NAME_MAX } from './sources.js';
import { LeadStageCodeSchema } from './stages.js';

export const LEAD_PUBLIC_CODE_REGEX = /^L-\d{5}$/;
export const LEAD_NAME_MAX = 200;
export const LEAD_PHONE_MAX = 32;
export const LEAD_EMAIL_MAX = 320;
export const LEAD_COMMENT_MAX = 2000;
/// CRM v4 fidelity (#2303) — «тема лида» cap. Matches the `Lead.title`
/// `VarChar(200)` column bound.
export const LEAD_TITLE_MAX = 200;
/// Free-text list search (`q`) cap — bounds the ILIKE pattern the
/// backend builds so a hostile caller cannot send an unbounded term.
export const LEAD_LIST_SEARCH_MAX = 200;

/**
 * @aiPurpose wire projection of a `Lead` row (#1076). Carries PII —
 *   audit diffs MUST redact `name`, `phone`, `email`, and `comment` via
 *   the backend `REDACT_PATHS` config.
 * @aiPii `name`, `phone`, `email`, `comment` are PII. The activity
 *   feed stores ids only; this projection is the full read for actors
 *   that already pass the Lead row-visibility check.
 * @aiInvariant `publicCode` matches `/^L-\d{5}$/` and is minted by
 *   the `lead_seq` Postgres sequence; UUID `id` is the primary key.
 */
export const LeadSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(LEAD_PUBLIC_CODE_REGEX),
    managerId: z.string().uuid().nullable(),
    clientId: z.string().uuid().nullable(),
    stageCode: LeadStageCodeSchema,
    archivedAt: z.string().datetime().nullable(),
    archivedById: z.string().uuid().nullable(),
    name: z.string().min(1).max(LEAD_NAME_MAX),
    /// E.164 enforced via the shared `phoneSchema` when present; NULL
    /// is allowed for partial intake.
    phone: phoneSchema.nullable(),
    email: z.string().email().max(LEAD_EMAIL_MAX).nullable(),
    comment: z.string().max(LEAD_COMMENT_MAX).nullable(),
    sourceId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type Lead = z.infer<typeof LeadSchema>;

/**
 * @aiPurpose typed cross-link fallback projection for a `Lead` shown
 *   to an actor who cannot see the full row (#1075 contract).
 * @aiInvariant per the cross-link allowlist, fallback exposes only
 *   `entityKind`, `publicCode`, and `status` (mapped from
 *   `stageCode`). Never includes name/phone/email/comment.
 */
export const LeadFallbackSchema = z
  .object({
    entityKind: z.literal('lead'),
    publicCode: z.string().regex(LEAD_PUBLIC_CODE_REGEX),
    stageCode: LeadStageCodeSchema,
  })
  .strict();
export type LeadFallback = z.infer<typeof LeadFallbackSchema>;

// ─── List query / response (#1117 slice A) ──────────────────────────────────

/**
 * @aiPurpose archive filter selector for `GET /api/leads`. Mirrors the
 *   `ClientArchiveFilterSchema` triple so callers can scope by lifecycle.
 *   `active` (default) omits archived rows from the list.
 */
export const LeadArchiveFilterSchema = z.enum(['active', 'archived', 'all']);
export type LeadArchiveFilter = z.infer<typeof LeadArchiveFilterSchema>;

/**
 * @aiPurpose minimal manager projection embedded in `LeadListItem`. Carries
 *   only the display fields the list table needs; ownership decisions
 *   still derive from the row's `managerId`, not this nested object.
 */
const LeadManagerRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();

/**
 * @aiPurpose PII-safe source projection embedded in `LeadListItem`
 *   (#1260). Carries only the display fields the workspace list needs.
 * @aiSecurity exposes `id` + `displayName` ONLY — never the HMAC secret
 *   or `secretIssuedAt`. The list/history surfaces must not widen this
 *   ref into the full `LeadSourceSchema`.
 */
const LeadSourceRefSchema = z
  .object({
    id: z.string().uuid(),
    displayName: z.string().min(1).max(LEAD_SOURCE_DISPLAY_NAME_MAX),
  })
  .strict();

/**
 * @aiPurpose row projection for `GET /api/leads`. Carries Lead PII —
 *   the list endpoint already enforces row-visibility, so callers who
 *   reach this shape have a confirmed read grant for every row.
 * @aiPii `name`, `phone` are PII; rendered only inside the workspace
 *   list. Cross-link consumers MUST use `LeadFallbackSchema` instead.
 * @aiInvariant `manager` is `null` exactly when `managerId === null`
 *   (Unassigned pool); `archivedAt` carries the canonical ISO timestamp
 *   when the row is archived; `activityCount` is the count of
 *   `LeadActivity` rows for this Lead (PII-safe — it's an int) and
 *   exists so the workspace list can render a "recent activity" badge
 *   without a follow-up request per row. `source` is `null` exactly
 *   when `sourceId === null`; `intakeSubmissionCount` is the count of
 *   `LeadIntakeSubmission` rows for this Lead (PII-safe int) so the list
 *   can flag external call-center intake without leaking the raw
 *   `comment`/`audioUrl` — those surface ONLY through the gated
 *   intake-history endpoint.
 */
export const LeadListItemSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(LEAD_PUBLIC_CODE_REGEX),
    name: z.string().min(1).max(LEAD_NAME_MAX),
    phone: phoneSchema.nullable(),
    stageCode: LeadStageCodeSchema,
    managerId: z.string().uuid().nullable(),
    manager: LeadManagerRefSchema.nullable(),
    clientId: z.string().uuid().nullable(),
    sourceId: z.string().uuid().nullable(),
    source: LeadSourceRefSchema.nullable(),
    archivedAt: z.string().datetime().nullable(),
    activityCount: z.number().int().min(0),
    intakeSubmissionCount: z.number().int().min(0),
    // WHY (#1596): opt-in count of China `Request` rows linked to this Lead
    //   AND visible to the actor — NOT a global FK count. Emitted ONLY when
    //   the detail read passes `?includeRequestCount=true`; optional so
    //   default list/detail payloads stay valid under `.strict()` for stale
    //   bundles. The reverse-read endpoint returns the matching summaries.
    requestCount: z.number().int().min(0).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type LeadListItem = z.infer<typeof LeadListItemSchema>;

/**
 * @aiPurpose query string for `GET /api/leads` — cursor-paginated list
 *   with stage filter, archive selector, and admin-only manager filter.
 * @aiInvariant `.strict()` blocks unknown keys. `archived` defaults to
 *   `'active'`. `managerId` is honored only for admin callers; the
 *   backend silently ignores it for non-admin and applies ownership
 *   scope. `limit` clamped to 1..100, default 50. `cursor` is opaque
 *   base64url(`{updatedAt, id}`). `q` is a trimmed free-text term the
 *   backend matches case-insensitively against name/phone/publicCode
 *   (parameterised ILIKE — never interpolated). `sourceId` narrows to a
 *   single source. `unassigned=true` narrows to the Unassigned pool
 *   (`managerId IS NULL`) WITHIN the caller's visible scope — it never
 *   widens visibility on its own. `clientId` narrows the list to a
 *   single bound client WITHIN the caller's visible scope — it never
 *   widens visibility on its own.
 */
export const GetLeadsQuerySchema = z
  .object({
    stageCode: LeadStageCodeSchema.optional(),
    managerId: z.string().uuid().optional(),
    q: z.string().trim().min(1).max(LEAD_LIST_SEARCH_MAX).optional(),
    sourceId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    unassigned: z.coerce.boolean().optional(),
    archived: LeadArchiveFilterSchema.default('active'),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type GetLeadsQuery = z.infer<typeof GetLeadsQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/leads`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying query
 *   returned ≤ `limit` rows; otherwise it carries the encoded
 *   `(updatedAt, id)` of the last returned row.
 */
export const GetLeadsResponseSchema = z
  .object({
    items: z.array(LeadListItemSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetLeadsResponse = z.infer<typeof GetLeadsResponseSchema>;

/**
 * @aiPurpose response envelope for `GET /api/leads/:id/intake-history`
 *   (#1260) — the external call-center intake submissions tied to a Lead,
 *   newest first.
 * @aiSecurity INTERNAL ONLY — the route enforces row-level Lead
 *   visibility BEFORE returning. Each item carries raw
 *   `phone`/`name`/`comment`/`audioUrl`; this is the ONLY place that PII
 *   leaves the server, and only to an actor already cleared to read the
 *   Lead. Never reuse this shape on the external agent boundary.
 */
export const GetLeadIntakeHistoryResponseSchema = z
  .object({
    items: z.array(LeadIntakeSubmissionHistoryItemSchema),
  })
  .strict();
export type GetLeadIntakeHistoryResponse = z.infer<typeof GetLeadIntakeHistoryResponseSchema>;

// ─── Create / Patch (#1118 slice B) ─────────────────────────────────────────

/**
 * @aiPurpose input normalizer for the optional lead `title` («тема лида»,
 *   #2303). Trims, caps at `LEAD_TITLE_MAX`, and normalizes an empty /
 *   whitespace-only value to `null` EXPLICITLY (unlike `comment`, `title` never
 *   stores `""`). Shared by create + patch so both write paths normalize
 *   identically. On patch an ABSENT `title` key stays absent (`.partial()` never
 *   injects it), so a patch without `title` leaves the stored value unchanged.
 */
const LeadTitleInputSchema = z
  .string()
  .trim()
  .max(LEAD_TITLE_MAX)
  .transform((v) => (v.length > 0 ? v : null));

/**
 * @aiPurpose input for `POST /api/leads` — manager-driven Lead create.
 *   The endpoint serves three product paths through one wire shape:
 *   raw (no client), bind-existing (`clientId`), or create-and-bind a
 *   new Client (`newClient`). The mutex between `clientId` and
 *   `newClient` is enforced via `.superRefine` so the wire stays a
 *   single endpoint instead of fanning out to two routes.
 * @aiInvariant `.strict()` blocks unknown keys; `clientId` and
 *   `newClient` are mutually exclusive — supplying both rejects with a
 *   400. `name` is required; all other lead fields are optional. The
 *   backend assigns `managerId = actor.sub` and `stageCode = 'new'`.
 * @aiPii `name`, `phone`, `email`, `comment` are PII. The activity feed
 *   row written for `LEAD_CREATED` carries id-only metadata; never
 *   snapshot these strings into `LeadActivity.metadata`.
 */
export const CreateLeadInputSchema = z
  .object({
    name: z.string().min(1).max(LEAD_NAME_MAX),
    phone: phoneSchema.nullable().optional(),
    email: z.string().email().max(LEAD_EMAIL_MAX).nullable().optional(),
    comment: z.string().max(LEAD_COMMENT_MAX).nullable().optional(),
    title: LeadTitleInputSchema.optional(),
    sourceId: z.string().uuid().nullable().optional(),
    clientId: z.string().uuid().optional(),
    newClient: CreateClientInputSchema.optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.clientId !== undefined && v.newClient !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'specify clientId or newClient, not both',
        path: ['clientId'],
      });
    }
  });
export type CreateLeadInput = z.infer<typeof CreateLeadInputSchema>;

/**
 * @aiPurpose input for `PATCH /api/leads/:id` — partial edit of the
 *   product-editable Lead fields. Stage transitions (#1119), claim /
 *   archive (#1120), and request-link integration (#1121) live on
 *   separate endpoints and are intentionally NOT patchable here.
 * @aiInvariant `nonEmptyPatch` rejects `{}` with a single
 *   `'patch must contain at least one field'`. `phone`/`email`/
 *   `comment`/`sourceId`/`clientId` accept `null` so the manager can
 *   clear the slot explicitly; unbinding `clientId` emits
 *   `LEAD_CLIENT_UNBOUND`, rebinding emits `LEAD_CLIENT_BOUND`.
 *   `managerId` and `stageCode` are deliberately excluded from this
 *   shape — they belong to the claim / stage-transition flows in later
 *   slices.
 */
const LeadPatchInnerSchema = z
  .object({
    name: z.string().min(1).max(LEAD_NAME_MAX),
    phone: phoneSchema.nullable(),
    email: z.string().email().max(LEAD_EMAIL_MAX).nullable(),
    comment: z.string().max(LEAD_COMMENT_MAX).nullable(),
    title: LeadTitleInputSchema,
    sourceId: z.string().uuid().nullable(),
    clientId: z.string().uuid().nullable(),
  })
  .strict();
export const PatchLeadInputSchema = nonEmptyPatch(LeadPatchInnerSchema.partial().strict());
export type PatchLeadInput = z.infer<typeof PatchLeadInputSchema>;

// ─── Stage transition (#1119 slice C) ───────────────────────────────────────

/**
 * @aiPurpose input for `PATCH /api/leads/:id/stage` — request the Lead
 *   to move into `toStage`. The stage graph is enforced server-side via
 *   `canTransitionLeadStage`; an illegal target rejects with a typed
 *   `LeadStageTransitionError`. Moving into `paid` additionally requires
 *   a bound Client (rejected with `LeadStagePaidRequiresClientError`).
 * @aiInvariant `.strict()` blocks unknown keys; `toStage` is one of the
 *   seven seeded codes. Stage transitions are NOT exposed through the
 *   generic PATCH `/api/leads/:id` endpoint because they have side
 *   effects (closing an existing active primary task, activity emission)
 *   that the field-edit path does not need.
 */
export const TransitionLeadStageInputSchema = z
  .object({
    toStage: LeadStageCodeSchema,
  })
  .strict();
export type TransitionLeadStageInput = z.infer<typeof TransitionLeadStageInputSchema>;

// ─── Ownership / lifecycle (#1120 slice D) ─────────────────────────────────

/**
 * @aiPurpose input for `POST /api/leads/:id/assign`. Runtime-ALL actors retain
 *   full reassignment; a `leads.assign` holder may assign only an eligible
 *   unowned pool Lead to an on-duty system Manager.
 * @aiInvariant `.strict()` blocks unknown keys; `managerId` is a UUID
 *   pointing at an existing User. The endpoint refuses no-op
 *   reassignments where the target equals the current owner.
 */
export const AssignLeadInputSchema = z
  .object({
    managerId: z.string().uuid(),
  })
  .strict();
export type AssignLeadInput = z.infer<typeof AssignLeadInputSchema>;

// ─── Duplicate detection / request link (#1121 slice E) ─────────────────────

/**
 * @aiPurpose PII-safe candidate slice returned by `POST /api/leads` when the
 *   submitted phone matches an existing Lead. The UI renders a warning so
 *   the manager can decide whether to claim/rebind the existing row instead
 *   of creating a parallel one.
 * @aiInvariant id-only projection — never carries `name`/`email`/`comment`.
 *   `phone` is intentionally omitted: the actor already knows the value
 *   (they just submitted it) and echoing it would put it in the response
 *   body for every duplicate group. The candidate set is computed only
 *   when `phone` is present on the create body and never auto-bound.
 * @aiPii safe — exposes only ids, public code, stage, ownership, archive flag.
 */
export const LeadPhoneDuplicateCandidateSchema = z
  .object({
    id: z.string().uuid(),
    publicCode: z.string().regex(LEAD_PUBLIC_CODE_REGEX),
    stageCode: LeadStageCodeSchema,
    managerId: z.string().uuid().nullable(),
    clientId: z.string().uuid().nullable(),
    archivedAt: z.string().datetime().nullable(),
  })
  .strict();
export type LeadPhoneDuplicateCandidate = z.infer<typeof LeadPhoneDuplicateCandidateSchema>;

/**
 * @aiPurpose response envelope for `POST /api/leads` (#1121 slice E).
 *   Wraps the created `LeadListItem` so the duplicate-warning slice can
 *   ride along on the same response without forcing a follow-up GET.
 * @aiInvariant `phoneDuplicates` is ALWAYS empty since #1361: global phone
 *   uniqueness hard-blocks a duplicate create with a 409
 *   `PHONE_ALREADY_EXISTS`, so a successful create can have no duplicates.
 *   The field is retained on the wire for compatibility until the consuming
 *   UI is retired (#1363).
 */
export const CreateLeadResponseSchema = z
  .object({
    lead: LeadListItemSchema,
    /**
     * @deprecated #1361 — always `[]`. A duplicate phone now hard-blocks the
     *   create with a 409 `PHONE_ALREADY_EXISTS` instead of being reported
     *   here. Kept on the wire until the warning UI is removed (#1363).
     */
    phoneDuplicates: z.array(LeadPhoneDuplicateCandidateSchema),
  })
  .strict();
export type CreateLeadResponse = z.infer<typeof CreateLeadResponseSchema>;
